import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";

import {
  fetchCurrentUser,
  readApiErrorCode,
  ApiErrorCode,
} from "../services/apiClient";

/**
 * The application user behind the Clerk session.
 *
 * A Clerk session says *who* someone is. This says what OMJ VISION knows about
 * them — their role, their status — and it comes from `GET /api/me`, which
 * reads MongoDB server-side. The role in this context is for rendering only:
 * every actual decision is made again by the backend on each request.
 */

const AppUserContext = createContext(null);

export const AppUserState = Object.freeze({
  LOADING: "loading",
  SIGNED_OUT: "signed_out",
  READY: "ready",
  NOT_PROVISIONED: "not_provisioned",
  DISABLED: "disabled",
  ERROR: "error",
});

export function AppUserProvider({ children }) {
  const { isLoaded, isSignedIn } = useAuth();

  const [appUser, setAppUser] = useState(null);
  const [state, setState] = useState(AppUserState.LOADING);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  useEffect(() => {
    if (!isLoaded) {
      setState(AppUserState.LOADING);
      return undefined;
    }

    if (!isSignedIn) {
      setAppUser(null);
      setState(AppUserState.SIGNED_OUT);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setState(AppUserState.LOADING);

    fetchCurrentUser({ signal: controller.signal })
      .then((user) => {
        if (!active) return;
        setAppUser(user);
        setState(AppUserState.READY);
      })
      .catch((error) => {
        if (!active || controller.signal.aborted) return;

        setAppUser(null);

        // The backend distinguishes "we have never heard of you" from "your
        // account is switched off"; the UI says something different for each.
        const code = readApiErrorCode(error);
        if (code === ApiErrorCode.NOT_PROVISIONED) {
          setState(AppUserState.NOT_PROVISIONED);
        } else if (code === ApiErrorCode.ACCOUNT_DISABLED) {
          setState(AppUserState.DISABLED);
        } else if (code === ApiErrorCode.AUTHENTICATION_REQUIRED) {
          setState(AppUserState.SIGNED_OUT);
        } else {
          setState(AppUserState.ERROR);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isLoaded, isSignedIn, reloadToken]);

  const value = useMemo(() => {
    const permissions = (appUser && appUser.permissions) || [];

    return {
      appUser,
      state,
      isLoading: state === AppUserState.LOADING,
      isReady: state === AppUserState.READY,
      role: (appUser && appUser.role) || null,
      permissions,
      hasPermission: (permission) => permissions.includes(permission),
      hasRole: (...roles) => Boolean(appUser) && roles.flat().includes(appUser.role),
      reload,
    };
  }, [appUser, state, reload]);

  return <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>;
}

/**
 * @returns {{appUser: object|null, state: string, isLoading: boolean, isReady: boolean, role: string|null, permissions: string[], hasPermission: (p: string) => boolean, hasRole: (...r: string[]) => boolean, reload: () => void}}
 */
export function useAppUser() {
  const context = useContext(AppUserContext);

  if (!context) {
    throw new Error("useAppUser must be used inside <AppUserProvider>.");
  }

  return context;
}

export default AppUserProvider;
